import React, { useState, useEffect } from 'react';
import { motion } from 'framer-motion';
import { 
  Zap, Plus, Trash2, Power, PowerOff, RefreshCw, 
  Sparkles, Wand2, Lightbulb, Send, Loader2, Check, ArrowRight
} from 'lucide-react';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '../components/ui/card';
import { Badge } from '../components/ui/badge';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Switch } from '../components/ui/switch';
import { Textarea } from '../components/ui/textarea';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogTrigger,
  DialogDescription,
} from '../components/ui/dialog';
import { Label } from '../components/ui/label';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '../components/ui/select';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../components/ui/tabs';
import { 
  getAutomationRules, createAutomationRule, updateAutomationRule, 
  deleteAutomationRule, aiGenerateRule, aiSuggestRules 
} from '../services/api';
import { toast } from 'sonner';

const triggerTypes = [
  { value: 'dns_block', label: 'DNS Block', icon: '🌐', description: 'Triggers on DNS-level threats' },
  { value: 'file_malicious', label: 'Malicious File', icon: '📁', description: 'Triggers on malware detection' },
  { value: 'email_phishing', label: 'Phishing Email', icon: '📧', description: 'Triggers on phishing attempts' },
  { value: 'app_anomaly', label: 'App Anomaly', icon: '⚙️', description: 'Triggers on suspicious apps' },
  { value: 'high_risk_score', label: 'High Risk Score', icon: '⚠️', description: 'Triggers when risk exceeds threshold' },
  { value: 'device_offline', label: 'Device Offline', icon: '📵', description: 'Triggers when device goes offline' },
  { value: 'multiple_failed_logins', label: 'Failed Logins', icon: '🔐', description: 'Triggers on multiple failed attempts' },
];

const actions = [
  { value: 'quarantine', label: 'Quarantine', description: 'Isolate the threat' },
  { value: 'block', label: 'Block', description: 'Prevent access' },
  { value: 'notify', label: 'Notify Admin', description: 'Send alert' },
  { value: 'lock_device', label: 'Lock Device', description: 'Lock affected device' },
  { value: 'isolate_network', label: 'Isolate Network', description: 'Disconnect from network' },
  { value: 'send_alert', label: 'Send Alert', description: 'Email notification' },
  { value: 'disable_user', label: 'Disable User', description: 'Disable user account' },
];

export default function Automation() {
  const [rules, setRules] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showAddDialog, setShowAddDialog] = useState(false);
  const [showAIDialog, setShowAIDialog] = useState(false);
  const [aiPrompt, setAIPrompt] = useState('');
  const [aiGenerating, setAIGenerating] = useState(false);
  const [aiSuggestions, setAISuggestions] = useState([]);
  const [loadingSuggestions, setLoadingSuggestions] = useState(false);
  const [generatedRule, setGeneratedRule] = useState(null);
  const [newRule, setNewRule] = useState({
    name: '',
    trigger_type: 'file_malicious',
    condition: 'risk_score >= 80',
    action: 'quarantine',
    enabled: true,
  });

  useEffect(() => {
    fetchData();
  }, []);

  const fetchData = async () => {
    setLoading(true);
    try {
      const response = await getAutomationRules();
      setRules(response.data);
    } catch (error) {
      console.error('Failed to fetch rules:', error);
      toast.error('Failed to load automation rules');
    } finally {
      setLoading(false);
    }
  };

  const handleAddRule = async (ruleData = newRule) => {
    if (!ruleData.name) {
      toast.error('Rule name is required');
      return;
    }
    try {
      await createAutomationRule(ruleData);
      toast.success('Rule created successfully');
      fetchData();
      setNewRule({ name: '', trigger_type: 'file_malicious', condition: 'risk_score >= 80', action: 'quarantine', enabled: true });
      setShowAddDialog(false);
      setShowAIDialog(false);
      setGeneratedRule(null);
    } catch (error) {
      toast.error('Failed to create rule');
    }
  };

  const handleToggleRule = async (rule) => {
    try {
      await updateAutomationRule(rule.id, { ...rule, enabled: !rule.enabled });
      toast.success(`Rule ${rule.enabled ? 'disabled' : 'enabled'}`);
      fetchData();
    } catch (error) {
      toast.error('Failed to update rule');
    }
  };

  const handleDeleteRule = async (ruleId) => {
    try {
      await deleteAutomationRule(ruleId);
      toast.success('Rule deleted');
      fetchData();
    } catch (error) {
      toast.error('Failed to delete rule');
    }
  };

  const handleAIGenerate = async () => {
    if (!aiPrompt.trim()) {
      toast.error('Please describe the rule you want to create');
      return;
    }
    
    setAIGenerating(true);
    try {
      const response = await aiGenerateRule(aiPrompt);
      if (response.data.success) {
        setGeneratedRule(response.data.rule);
        toast.success('AI generated a rule suggestion!');
      } else {
        toast.error(response.data.error || 'AI generation failed');
      }
    } catch (error) {
      toast.error('Failed to generate rule with AI');
    } finally {
      setAIGenerating(false);
    }
  };

  const handleGetSuggestions = async () => {
    setLoadingSuggestions(true);
    try {
      const response = await aiSuggestRules();
      if (response.data.success) {
        setAISuggestions(response.data.suggestions);
        toast.success('AI generated suggestions based on your threat data');
      } else {
        toast.error(response.data.error || 'Failed to get suggestions');
      }
    } catch (error) {
      toast.error('Failed to get AI suggestions');
    } finally {
      setLoadingSuggestions(false);
    }
  };

  const handleApplySuggestion = async (suggestion) => {
    const rule = {
      name: suggestion.name,
      trigger_type: suggestion.trigger_type,
      condition: suggestion.condition,
      action: suggestion.action,
      enabled: true,
    };
    await handleAddRule(rule);
    setAISuggestions(aiSuggestions.filter(s => s.name !== suggestion.name));
  };

  const getTriggerInfo = (type) => triggerTypes.find((t) => t.value === type) || { label: type, icon: '⚡' };

  return (
    <div className="space-y-6" data-testid="automation-page">
      {/* Header */}
      <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-4">
        <div>
          <h1 className="font-heading font-bold text-3xl text-white">Automation</h1>
          <p className="text-slate-400 mt-1">Configure AI-powered security playbooks</p>
        </div>
        <div className="flex gap-2">
          <Button variant="outline" onClick={fetchData} className="border-slate-700">
            <RefreshCw className="w-4 h-4 mr-2" />
            Refresh
          </Button>
          
          {/* AI Create Button */}
          <Dialog open={showAIDialog} onOpenChange={setShowAIDialog}>
            <DialogTrigger asChild>
              <Button className="bg-gradient-to-r from-purple-600 to-blue-600 hover:from-purple-700 hover:to-blue-700">
                <Sparkles className="w-4 h-4 mr-2" />
                AI Create
              </Button>
            </DialogTrigger>
            <DialogContent className="bg-slate-900 border-slate-800 max-w-2xl">
              <DialogHeader>
                <DialogTitle className="text-white flex items-center gap-2">
                  <Wand2 className="w-5 h-5 text-purple-400" />
                  AI Rule Generator
                </DialogTitle>
                <DialogDescription className="text-slate-400">
                  Describe what you want to automate in plain English
                </DialogDescription>
              </DialogHeader>
              
              <Tabs defaultValue="generate" className="mt-4">
                <TabsList className="bg-slate-800 border border-slate-700">
                  <TabsTrigger value="generate" className="data-[state=active]:bg-slate-700">
                    <Wand2 className="w-4 h-4 mr-2" />
                    Generate Rule
                  </TabsTrigger>
                  <TabsTrigger value="suggest" className="data-[state=active]:bg-slate-700">
                    <Lightbulb className="w-4 h-4 mr-2" />
                    AI Suggestions
                  </TabsTrigger>
                </TabsList>
                
                {/* Generate Tab */}
                <TabsContent value="generate" className="space-y-4 mt-4">
                  <div className="space-y-2">
                    <Label className="text-slate-300">Describe your automation rule</Label>
                    <Textarea
                      value={aiPrompt}
                      onChange={(e) => setAIPrompt(e.target.value)}
                      placeholder="e.g., Block all files with risk score above 90, quarantine emails from unknown senders with suspicious attachments, notify me when there are more than 5 failed login attempts..."
                      className="bg-slate-800 border-slate-700 text-white min-h-[100px]"
                    />
                  </div>
                  
                  <div className="flex gap-2">
                    <Button 
                      onClick={handleAIGenerate} 
                      disabled={aiGenerating}
                      className="flex-1 bg-purple-600 hover:bg-purple-700"
                    >
                      {aiGenerating ? (
                        <><Loader2 className="w-4 h-4 mr-2 animate-spin" /> Generating...</>
                      ) : (
                        <><Sparkles className="w-4 h-4 mr-2" /> Generate Rule</>
                      )}
                    </Button>
                  </div>
                  
                  {/* Generated Rule Preview */}
                  {generatedRule && (
                    <motion.div
                      initial={{ opacity: 0, y: 10 }}
                      animate={{ opacity: 1, y: 0 }}
                      className="p-4 rounded-lg bg-slate-800/50 border border-purple-500/30"
                    >
                      <div className="flex items-center gap-2 mb-3">
                        <Check className="w-4 h-4 text-emerald-500" />
                        <span className="text-sm font-medium text-emerald-400">AI Generated Rule</span>
                      </div>
                      
                      <div className="space-y-3">
                        <div className="flex justify-between">
                          <span className="text-slate-400">Name</span>
                          <span className="text-white font-medium">{generatedRule.name}</span>
                        </div>
                        <div className="flex justify-between">
                          <span className="text-slate-400">Trigger</span>
                          <Badge className="bg-blue-500/10 text-blue-400">
                            {getTriggerInfo(generatedRule.trigger_type).icon} {getTriggerInfo(generatedRule.trigger_type).label}
                          </Badge>
                        </div>
                        <div className="flex justify-between">
                          <span className="text-slate-400">Condition</span>
                          <code className="text-purple-400 text-sm">{generatedRule.condition}</code>
                        </div>
                        <div className="flex justify-between">
                          <span className="text-slate-400">Action</span>
                          <Badge className="bg-amber-500/10 text-amber-400">{generatedRule.action}</Badge>
                        </div>
                      </div>
                      
                      <div className="flex gap-2 mt-4">
                        <Button 
                          variant="outline" 
                          onClick={() => setGeneratedRule(null)}
                          className="flex-1 border-slate-700"
                        >
                          Discard
                        </Button>
                        <Button 
                          onClick={() => handleAddRule(generatedRule)}
                          className="flex-1 bg-emerald-600 hover:bg-emerald-700"
                        >
                          <Check className="w-4 h-4 mr-2" />
                          Apply Rule
                        </Button>
                      </div>
                    </motion.div>
                  )}
                  
                  {/* Example prompts */}
                  <div className="pt-2">
                    <p className="text-xs text-slate-500 mb-2">Example prompts:</p>
                    <div className="flex flex-wrap gap-2">
                      {[
                        'Block files with malware signatures',
                        'Alert on suspicious login patterns',
                        'Quarantine phishing emails automatically',
                      ].map((prompt, i) => (
                        <button
                          key={i}
                          onClick={() => setAIPrompt(prompt)}
                          className="text-xs px-2 py-1 rounded bg-slate-800 text-slate-400 hover:text-white hover:bg-slate-700 transition-colors"
                        >
                          {prompt}
                        </button>
                      ))}
                    </div>
                  </div>
                </TabsContent>
                
                {/* Suggestions Tab */}
                <TabsContent value="suggest" className="space-y-4 mt-4">
                  <div className="p-4 rounded-lg bg-blue-500/10 border border-blue-500/20">
                    <p className="text-sm text-blue-300">
                      <Lightbulb className="w-4 h-4 inline mr-2" />
                      AI will analyze your threat data and suggest automation rules tailored to your security needs.
                    </p>
                  </div>
                  
                  <Button 
                    onClick={handleGetSuggestions} 
                    disabled={loadingSuggestions}
                    className="w-full bg-blue-600 hover:bg-blue-700"
                  >
                    {loadingSuggestions ? (
                      <><Loader2 className="w-4 h-4 mr-2 animate-spin" /> Analyzing your data...</>
                    ) : (
                      <><Sparkles className="w-4 h-4 mr-2" /> Get AI Suggestions</>
                    )}
                  </Button>
                  
                  {/* Suggestions List */}
                  {aiSuggestions.length > 0 && (
                    <div className="space-y-3">
                      {aiSuggestions.map((suggestion, index) => (
                        <motion.div
                          key={index}
                          initial={{ opacity: 0, x: -10 }}
                          animate={{ opacity: 1, x: 0 }}
                          transition={{ delay: index * 0.1 }}
                          className="p-4 rounded-lg bg-slate-800/50 border border-slate-700"
                        >
                          <div className="flex items-start justify-between">
                            <div className="flex-1">
                              <h4 className="font-medium text-white">{suggestion.name}</h4>
                              <p className="text-sm text-slate-400 mt-1">{suggestion.reason}</p>
                              <div className="flex items-center gap-2 mt-2">
                                <Badge className="bg-blue-500/10 text-blue-400 text-xs">
                                  {getTriggerInfo(suggestion.trigger_type).label}
                                </Badge>
                                <ArrowRight className="w-3 h-3 text-slate-500" />
                                <Badge className="bg-amber-500/10 text-amber-400 text-xs">
                                  {suggestion.action}
                                </Badge>
                              </div>
                            </div>
                            <Button 
                              size="sm" 
                              onClick={() => handleApplySuggestion(suggestion)}
                              className="bg-emerald-600 hover:bg-emerald-700"
                            >
                              Apply
                            </Button>
                          </div>
                        </motion.div>
                      ))}
                    </div>
                  )}
                </TabsContent>
              </Tabs>
            </DialogContent>
          </Dialog>
          
          {/* Manual Add Button */}
          <Dialog open={showAddDialog} onOpenChange={setShowAddDialog}>
            <DialogTrigger asChild>
              <Button className="bg-blue-600 hover:bg-blue-700" data-testid="add-rule-btn">
                <Plus className="w-4 h-4 mr-2" />
                Add Rule
              </Button>
            </DialogTrigger>
            <DialogContent className="bg-slate-900 border-slate-800">
              <DialogHeader>
                <DialogTitle className="text-white">Create Automation Rule</DialogTitle>
              </DialogHeader>
              <div className="space-y-4">
                <div className="space-y-2">
                  <Label className="text-slate-300">Rule Name</Label>
                  <Input
                    placeholder="Auto-quarantine critical files"
                    value={newRule.name}
                    onChange={(e) => setNewRule({ ...newRule, name: e.target.value })}
                    className="bg-slate-800 border-slate-700 text-white"
                  />
                </div>
                <div className="space-y-2">
                  <Label className="text-slate-300">Trigger</Label>
                  <Select value={newRule.trigger_type} onValueChange={(v) => setNewRule({ ...newRule, trigger_type: v })}>
                    <SelectTrigger className="bg-slate-800 border-slate-700 text-white">
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent className="bg-slate-800 border-slate-700">
                      {triggerTypes.map((t) => (
                        <SelectItem key={t.value} value={t.value}>
                          <span className="mr-2">{t.icon}</span> {t.label}
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>
                <div className="space-y-2">
                  <Label className="text-slate-300">Condition</Label>
                  <Input
                    placeholder="risk_score >= 80"
                    value={newRule.condition}
                    onChange={(e) => setNewRule({ ...newRule, condition: e.target.value })}
                    className="bg-slate-800 border-slate-700 text-white font-mono text-sm"
                  />
                  <p className="text-xs text-slate-500">
                    Use variables: risk_score, classification, category, count
                  </p>
                </div>
                <div className="space-y-2">
                  <Label className="text-slate-300">Action</Label>
                  <Select value={newRule.action} onValueChange={(v) => setNewRule({ ...newRule, action: v })}>
                    <SelectTrigger className="bg-slate-800 border-slate-700 text-white">
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent className="bg-slate-800 border-slate-700">
                      {actions.map((a) => (
                        <SelectItem key={a.value} value={a.value}>{a.label}</SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>
                <Button onClick={() => handleAddRule()} className="w-full bg-blue-600 hover:bg-blue-700">
                  Create Rule
                </Button>
              </div>
            </DialogContent>
          </Dialog>
        </div>
      </div>

      {/* Stats */}
      <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
        <Card className="bg-slate-900 border-slate-800">
          <CardContent className="p-4">
            <div className="flex items-center gap-3">
              <div className="w-10 h-10 rounded-lg bg-blue-500/10 flex items-center justify-center">
                <Zap className="w-5 h-5 text-blue-500" />
              </div>
              <div>
                <p className="text-2xl font-heading font-bold text-white">{rules.length}</p>
                <p className="text-xs text-slate-500">Total Rules</p>
              </div>
            </div>
          </CardContent>
        </Card>
        <Card className="bg-slate-900 border-slate-800">
          <CardContent className="p-4">
            <div className="flex items-center gap-3">
              <div className="w-10 h-10 rounded-lg bg-emerald-500/10 flex items-center justify-center">
                <Power className="w-5 h-5 text-emerald-500" />
              </div>
              <div>
                <p className="text-2xl font-heading font-bold text-white">{rules.filter((r) => r.enabled).length}</p>
                <p className="text-xs text-slate-500">Active</p>
              </div>
            </div>
          </CardContent>
        </Card>
        <Card className="bg-slate-900 border-slate-800">
          <CardContent className="p-4">
            <div className="flex items-center gap-3">
              <div className="w-10 h-10 rounded-lg bg-slate-500/10 flex items-center justify-center">
                <PowerOff className="w-5 h-5 text-slate-500" />
              </div>
              <div>
                <p className="text-2xl font-heading font-bold text-white">{rules.filter((r) => !r.enabled).length}</p>
                <p className="text-xs text-slate-500">Disabled</p>
              </div>
            </div>
          </CardContent>
        </Card>
        <Card className="bg-slate-900 border-slate-800">
          <CardContent className="p-4">
            <div className="flex items-center gap-3">
              <div className="w-10 h-10 rounded-lg bg-purple-500/10 flex items-center justify-center">
                <Sparkles className="w-5 h-5 text-purple-500" />
              </div>
              <div>
                <p className="text-2xl font-heading font-bold text-white">AI</p>
                <p className="text-xs text-slate-500">Powered</p>
              </div>
            </div>
          </CardContent>
        </Card>
      </div>

      {/* Rules List */}
      <div className="space-y-4">
        {rules.length === 0 ? (
          <Card className="bg-slate-900 border-slate-800">
            <CardContent className="p-12 text-center">
              <Zap className="w-12 h-12 text-slate-600 mx-auto mb-4" />
              <h3 className="text-lg font-medium text-white mb-2">No automation rules yet</h3>
              <p className="text-slate-400 mb-4">Create your first rule to automate security responses</p>
              <div className="flex justify-center gap-3">
                <Button onClick={() => setShowAIDialog(true)} className="bg-purple-600 hover:bg-purple-700">
                  <Sparkles className="w-4 h-4 mr-2" />
                  AI Create
                </Button>
                <Button onClick={() => setShowAddDialog(true)} variant="outline" className="border-slate-700">
                  <Plus className="w-4 h-4 mr-2" />
                  Manual Create
                </Button>
              </div>
            </CardContent>
          </Card>
        ) : (
          rules.map((rule, index) => {
            const trigger = getTriggerInfo(rule.trigger_type);
            return (
              <motion.div
                key={rule.id}
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ delay: index * 0.05 }}
              >
                <Card className={`bg-slate-900 border-slate-800 ${!rule.enabled ? 'opacity-60' : ''}`}>
                  <CardContent className="p-5">
                    <div className="flex items-center justify-between">
                      <div className="flex items-center gap-4">
                        <div className="w-12 h-12 rounded-lg bg-slate-800 flex items-center justify-center text-2xl">
                          {trigger.icon}
                        </div>
                        <div>
                          <h3 className="font-medium text-white">{rule.name}</h3>
                          <p className="text-sm text-slate-500">
                            When <span className="text-slate-300">{trigger.label}</span> and{' '}
                            <span className="font-mono text-xs text-slate-400">{rule.condition}</span>{' '}
                            then <span className="text-blue-400">{rule.action}</span>
                          </p>
                        </div>
                      </div>
                      <div className="flex items-center gap-4">
                        <div className="flex items-center gap-2">
                          <Switch
                            checked={rule.enabled}
                            onCheckedChange={() => handleToggleRule(rule)}
                          />
                          <span className={`text-sm ${rule.enabled ? 'text-emerald-500' : 'text-slate-500'}`}>
                            {rule.enabled ? 'Active' : 'Disabled'}
                          </span>
                        </div>
                        <Button
                          variant="ghost"
                          size="icon"
                          onClick={() => handleDeleteRule(rule.id)}
                          className="text-slate-400 hover:text-red-500"
                        >
                          <Trash2 className="w-4 h-4" />
                        </Button>
                      </div>
                    </div>
                  </CardContent>
                </Card>
              </motion.div>
            );
          })
        )}
      </div>
    </div>
  );
}
